import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { Copy, Trash2, UserPlus, RefreshCw } from 'lucide-react';
import apiClient from '../../utils/apiClient';

interface InviteCode {
  id: number;
  code: string;
  role_name: string;
  created_by_name: string;
  used_by_name: string | null;
  expires_at: string | null;
  used_at: string | null;
  created_at: string;
  is_used: boolean;
  is_expired: boolean;
}

export const InviteManagement: React.FC = () => {
  const [invites, setInvites] = useState<InviteCode[]>([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [roleName, setRoleName] = useState('viewer');
  const [expiresInDays, setExpiresInDays] = useState<number | ''>('');

  const fetchInvites = async () => {
    setLoading(true);
    try {
      const response = await apiClient.get('/invites');
      setInvites(response.data.invites || []);
    } catch (error) {
      console.error('Failed to fetch invites:', error);
      toast.error('Failed to load invite codes');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInvites();
  }, []);

  const createInvite = async () => {
    setCreating(true);
    try {
      const payload: any = { roleName };
      if (expiresInDays) {
        payload.expiresInDays = expiresInDays;
      }

      await apiClient.post('/invites', payload);
      toast.success('Invite code created!');
      setExpiresInDays('');
      fetchInvites();
    } catch (error) {
      console.error('Failed to create invite:', error);
      toast.error('Failed to create invite code');
    } finally {
      setCreating(false);
    }
  };

  const revokeInvite = async (code: string) => {
    if (!window.confirm('Are you sure you want to revoke this invite code?')) {
      return;
    }

    try {
      await apiClient.delete(`/invites/${code}`);
      toast.success('Invite code revoked');
      fetchInvites();
    } catch (error) {
      console.error('Failed to revoke invite:', error);
      toast.error('Failed to revoke invite code');
    }
  };

  const copyInviteLink = (code: string) => {
    const link = `${window.location.origin}/signup?invite=${code}`;
    navigator.clipboard.writeText(link);
    toast.success('Invite link copied to clipboard!');
  };

  const copyCode = (code: string) => {
    navigator.clipboard.writeText(code);
    toast.success('Code copied to clipboard!');
  };

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getStatusBadge = (invite: InviteCode) => {
    if (invite.is_used) {
      return <span className="badge bg-secondary">Used</span>;
    }
    if (invite.is_expired) {
      return <span className="badge bg-danger">Expired</span>;
    }
    return <span className="badge bg-success">Active</span>;
  };

  return (
    <div className="invite-management">
      <div className="mb-4">
        <h5>Create New Invite</h5>
        <p className="text-muted small">
          Choose choir viewer or staff scheduling access, then copy the invitation link to share.
        </p>

        <div className="row g-2 align-items-end">
          <div className="col-auto">
            <label className="form-label small" htmlFor="invite-role">Access</label>
            <select id="invite-role" className="form-select form-select-sm" value={roleName} onChange={e => setRoleName(e.target.value)}>
              <option value="viewer">Choir viewer</option><option value="staff">Staff scheduling</option>
            </select>
          </div>
          <div className="col-auto">
            <label className="form-label small">Expires in (days)</label>
            <input
              type="number"
              className="form-control form-control-sm"
              style={{ width: '100px' }}
              placeholder="Never"
              value={expiresInDays}
              onChange={(e) => setExpiresInDays(e.target.value ? parseInt(e.target.value) : '')}
              min={1}
            />
          </div>
          <div className="col-auto">
            <button
              className="btn btn-primary btn-sm"
              onClick={createInvite}
              disabled={creating}
            >
              <UserPlus size={16} className="me-1" />
              {creating ? 'Creating...' : 'Create Invite'}
            </button>
          </div>
          <div className="col-auto">
            <button
              className="btn btn-outline-secondary btn-sm"
              onClick={fetchInvites}
              disabled={loading}
            >
              <RefreshCw size={16} className={loading ? 'spin' : ''} />
            </button>
          </div>
        </div>
      </div>

      <h5>Invite Codes</h5>

      {loading ? (
        <div className="text-center py-4">
          <div className="spinner-border spinner-border-sm" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      ) : invites.length === 0 ? (
        <div className="alert alert-info">
          No invite codes yet. Create one above to invite users.
        </div>
      ) : (
        <div className="table-responsive">
          <table className="table table-sm">
            <thead>
              <tr>
                <th>Code</th>
                <th>Access</th><th>Status</th>
                <th>Created</th>
                <th>Expires</th>
                <th>Used By</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {invites.map((invite) => (
                <tr key={invite.id} className={invite.is_used || invite.is_expired ? 'table-secondary' : ''}>
                  <td>
                    <code className="small">{invite.code.substring(0, 8)}...</code>
                  </td>
                  <td>{invite.role_name === 'staff' ? 'Staff' : invite.role_name}</td><td>{getStatusBadge(invite)}</td>
                  <td className="small">{formatDate(invite.created_at)}</td>
                  <td className="small">{formatDate(invite.expires_at)}</td>
                  <td className="small">{invite.used_by_name || '-'}</td>
                  <td>
                    {!invite.is_used && !invite.is_expired && (
                      <>
                        <button
                          className="btn btn-outline-primary btn-sm me-1"
                          onClick={() => copyInviteLink(invite.code)}
                          title="Copy invite link"
                        >
                          <Copy size={14} />
                        </button>
                        <button
                          className="btn btn-outline-danger btn-sm"
                          onClick={() => revokeInvite(invite.code)}
                          title="Revoke invite"
                        >
                          <Trash2 size={14} />
                        </button>
                      </>
                    )}
                    {(invite.is_used || invite.is_expired) && (
                      <button
                        className="btn btn-outline-secondary btn-sm"
                        onClick={() => copyCode(invite.code)}
                        title="Copy code"
                      >
                        <Copy size={14} />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <style>{`
        .spin {
          animation: spin 1s linear infinite;
        }
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default InviteManagement;
